﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Plus, Pencil, Users, Paperclip, BarChart3, Archive, ArchiveRestore, Trash2, Settings, FileText,
} from 'lucide-react';
import type { ApiError, SopListItem, SopStatusCode } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { sopService } from '@/services/sop.service';
import { usePermission } from '@/hooks/usePermission';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { DataTable, type Column } from '@/components/ui/data-table';
import { StatCard } from '@/components/ui/stat-card';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { Tabs } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/dateFormat';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SopFormDialog } from './components/SopFormDialog';
import { AssignDialog } from './components/AssignDialog';
import { DocumentsDialog } from './components/DocumentsDialog';
import { ComplianceDialog } from './components/ComplianceDialog';
import { CategoriesDialog } from './components/CategoriesDialog';

const ALL = '__all__';
const STATUS_VARIANT: Record<SopStatusCode, 'secondary' | 'success' | 'outline'> = {
  draft: 'secondary',
  active: 'success',
  archived: 'outline',
};

const STATUS_TABS = ['all', 'draft', 'active', 'archived'] as const;
type StatusTab = (typeof STATUS_TABS)[number];
const STATUS_TAB_LABELS: Record<StatusTab, string> = {
  all: 'All', draft: 'Draft', active: 'Active', archived: 'Archived',
};

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

type ActiveDialog =
  | { type: 'form'; sopId: string | null }
  | { type: 'assign'; sop: SopListItem }
  | { type: 'documents'; sop: SopListItem }
  | { type: 'compliance'; sop: SopListItem }
  | { type: 'categories' }
  | null;

export function HrmsSopPage() {
  const perms = usePermission();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [categoryId, setCategoryId] = useState<string>(ALL);
  const [dialog, setDialog] = useState<ActiveDialog>(null);
  const [pendingDeleteSop, setPendingDeleteSop] = useState<string | null>(null);

  const params = {
    page,
    limit: 10,
    q: q || undefined,
    status: statusTab === 'all' ? undefined : statusTab,
    category_id: categoryId === ALL ? undefined : categoryId,
  };

  const listQuery = useQuery({
    queryKey: ['sop', 'sops', params],
    queryFn: () => sopService.listSops(params),
  });

  const statsQuery = useQuery({
    queryKey: ['sop', 'stats'],
    queryFn: () => sopService.stats(),
  });

  const categoriesQuery = useQuery({
    queryKey: ['sop', 'categories'],
    queryFn: () => sopService.listCategories(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sop', 'sops'] });
    void queryClient.invalidateQueries({ queryKey: ['sop', 'stats'] });
  };

  const setStatusMut = useMutation({
    mutationFn: ({ id, next }: { id: string; next: SopStatusCode }) => sopService.setStatus(id, next),
    onSuccess: () => { toast.success('Status updated'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => sopService.deleteSop(id),
    onSuccess: () => { toast.success('SOP deleted'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const canManage = perms.has(PERMISSIONS.SOP_MANAGE);
  const canAssign = perms.has(PERMISSIONS.SOP_ASSIGN);
  const canCompliance = perms.has(PERMISSIONS.SOP_COMPLIANCE_VIEW);

  const columns: Column<SopListItem>[] = [
    {
      key: 'title',
      header: 'Title',
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{r.title}</p>
          <p className="text-xs text-muted-foreground">{r.sop_code}</p>
        </div>
      ),
    },
    { key: 'category', header: 'Category', cell: (r) => r.category_name ?? <span className="text-muted-foreground">—</span> },
    { key: 'status', header: 'Status', cell: (r) => <Badge variant={STATUS_VARIANT[r.status]} className="capitalize">{r.status}</Badge> },
    { key: 'version', header: 'Version', cell: (r) => `v${r.version}` },
    {
      key: 'compliance',
      header: 'Acknowledged',
      cell: (r) => (
        <span className="text-sm">
          {r.acknowledged_count}/{r.audience_count}
        </span>
      ),
    },
    { key: 'created', header: 'Created', cell: (r) => <span className="text-muted-foreground">{formatDate(r.created_at)}</span> },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          {canManage && (
            <button type="button" onClick={() => setDialog({ type: 'form', sopId: r.id })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
          )}
          {canAssign && (
            <button type="button" onClick={() => setDialog({ type: 'assign', sop: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Assign"><Users className="h-4 w-4" /></button>
          )}
          {canManage && (
            <button type="button" onClick={() => setDialog({ type: 'documents', sop: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Documents"><Paperclip className="h-4 w-4" /></button>
          )}
          {canCompliance && (
            <button type="button" onClick={() => setDialog({ type: 'compliance', sop: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Compliance"><BarChart3 className="h-4 w-4" /></button>
          )}
          {canManage && r.status !== 'archived' && (
            <button type="button" onClick={() => setStatusMut.mutate({ id: r.id, next: r.status === 'active' ? 'archived' : 'active' })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={r.status === 'active' ? 'Archive' : 'Activate'}>
              {r.status === 'active' ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
            </button>
          )}
          {canManage && r.status === 'archived' && (
            <button type="button" onClick={() => setStatusMut.mutate({ id: r.id, next: 'active' })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Restore"><ArchiveRestore className="h-4 w-4" /></button>
          )}
          {canManage && (
            <button type="button" onClick={() => setPendingDeleteSop(r.id)} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
          )}
        </div>
      ),
    },
  ];

  const stats = statsQuery.data;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">SOP Management</h1>
          <p className="text-sm text-muted-foreground">Create, manage and assign Standard Operating Procedures</p>
        </div>
        <div className="flex gap-2">
          <PermissionGate permission={PERMISSIONS.SOP_CONFIG}>
            <Button variant="outline" onClick={() => setDialog({ type: 'categories' })}>
              <Settings className="h-4 w-4" /> Categories
            </Button>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.SOP_MANAGE}>
            <Button onClick={() => setDialog({ type: 'form', sopId: null })}>
              <Plus className="h-4 w-4" /> Create SOP
            </Button>
          </PermissionGate>
        </div>
      </div>

      {canCompliance && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total SOPs" value={stats?.total_sops ?? '—'} icon={FileText} tone="teal" />
          <StatCard label="Active" value={stats?.by_status.active ?? '—'} icon={ArchiveRestore} tone="emerald" />
          <StatCard label="Drafts" value={stats?.by_status.draft ?? '—'} icon={Pencil} tone="amber" />
          <StatCard label="Overdue Acks" value={stats?.overdue_count ?? '—'} icon={BarChart3} tone="rose" />
        </div>
      )}

      {/* Filters — same toolbar layout as the Notice admin page. */}
      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          tabs={STATUS_TABS.map((tab) => ({ key: tab, label: STATUS_TAB_LABELS[tab] }))}
          active={statusTab}
          onChange={(k) => { setStatusTab(k as StatusTab); setPage(1); }}
        />

        <Select value={categoryId} onValueChange={(v) => { setCategoryId(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-full sm:w-[160px] text-xs">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Categories</SelectItem>
            {(categoriesQuery.data ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="w-full sm:ml-auto sm:w-[220px]">
          <SearchInput
            value={q}
            onChange={(v) => { setQ(v); setPage(1); }}
            placeholder="Search SOPs…"
            className="h-8 text-xs"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={listQuery.data?.data ?? []}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => listQuery.refetch()}
        empty="No SOPs yet. Create your first SOP to get started."
        pagination={{
          page,
          totalPages: listQuery.data?.pagination.totalPages ?? 1,
          total: listQuery.data?.pagination.total,
          onPageChange: setPage,
        }}
      />

      {dialog?.type === 'form' && <SopFormDialog sopId={dialog.sopId} onClose={() => setDialog(null)} />}
      {dialog?.type === 'assign' && <AssignDialog sopId={dialog.sop.id} sopTitle={dialog.sop.title} onClose={() => setDialog(null)} />}
      {dialog?.type === 'documents' && <DocumentsDialog sopId={dialog.sop.id} sopTitle={dialog.sop.title} onClose={() => setDialog(null)} />}
      {dialog?.type === 'compliance' && <ComplianceDialog sopId={dialog.sop.id} sopTitle={dialog.sop.title} onClose={() => setDialog(null)} />}
      {dialog?.type === 'categories' && <CategoriesDialog onClose={() => setDialog(null)} />}

      <ConfirmDialog
        open={!!pendingDeleteSop}
        onOpenChange={(o) => { if (!o) setPendingDeleteSop(null); }}
        title="Delete this SOP?"
        description="This SOP will be permanently deleted."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDeleteSop) deleteMut.mutate(pendingDeleteSop); }}
      />
    </div>
  );
}
